// Manager self-service analytics (Phase 2e) — a read-only snapshot of the
// manager's direct team: who's in/out today, what's awaiting them, OKR mix and
// upcoming events.
import { useState, useEffect } from 'react';
import { Users, UserCheck, Inbox, CalendarOff, Plane, Cake, Award } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <Card style={{ padding: 16 }} className="flex items-center gap-3">
      <div style={{ width: 42, height: 42, borderRadius: 12, background: tone || AG.primaryLightest, color: AG.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={20} /></div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: AG.text, lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 12, color: AG.n500, marginTop: 3 }}>{label}</div>
      </div>
    </Card>
  );
}

function ListCard({ icon: Icon, title, rows, render, empty }) {
  return (
    <Card style={{ padding: 18 }}>
      <div className="flex items-center gap-2" style={{ marginBottom: 12 }}><Icon size={17} style={{ color: AG.primary }} /><span style={{ fontWeight: 600, color: AG.text }}>{title}</span></div>
      {rows.length === 0 ? <p style={{ fontSize: 13, color: AG.n500 }}>{empty}</p> : <div style={{ display: 'grid', gap: 8 }}>{rows.map(render)}</div>}
    </Card>
  );
}

const eventLabel = (e) => (e.inDays === 0 ? 'Today' : e.inDays === 1 ? 'Tomorrow' : `in ${e.inDays} days`);

export default function TeamAnalytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.teamAnalytics().then(setD).catch(() => setD(null)); }, []);

  if (!d) return <CenterSpinner />;
  if (d.headcount === 0)
    return (
      <div className="fade-up"><SectionTitle title="Team Analytics" sub="Your team at a glance" />
        <Card><EmptyState icon={Users} title="No direct reports" desc="Team analytics appear once people report to you." /></Card>
      </div>
    );

  const okrEntries = Object.entries(d.okrStatus || {});
  const pending = d.pending.leaves + d.pending.requests;

  return (
    <div className="space-y-4 fade-up">
      <SectionTitle title="Team Analytics" sub="Your team at a glance" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Users} label="Team size" value={d.headcount} />
        <Stat icon={UserCheck} label="Present today" value={d.presentToday} tone="#e8f5ec" />
        <Stat icon={CalendarOff} label="On leave today" value={d.onLeaveToday.length} tone="#fdeede" />
        <Stat icon={Inbox} label="Awaiting you" value={pending} tone={pending ? '#fde8e8' : undefined} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ListCard icon={CalendarOff} title="On leave today" rows={d.onLeaveToday} empty="Everyone's in today."
          render={(l) => <div key={l.id} className="flex items-center justify-between"><span style={{ fontSize: 13.5, color: AG.text }}>{l.name}</span><Pill tone="warning">{l.type}</Pill></div>} />
        <ListCard icon={Plane} title="Out of office" rows={d.outOfOffice} empty="No one is out of office."
          render={(o) => <div key={o.id} className="flex items-center justify-between"><span style={{ fontSize: 13.5, color: AG.text }}>{o.name}</span><span style={{ fontSize: 12, color: AG.n500 }}>{o.from} → {o.to}</span></div>} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ListCard icon={Cake} title="Upcoming events" rows={d.upcoming} empty="Nothing in the next 30 days."
          render={(e) => (
            <div key={`${e.id}-${e.kind}`} className="flex items-center justify-between">
              <span className="flex items-center gap-2" style={{ fontSize: 13.5, color: AG.text }}>
                {e.kind === 'Birthday' ? <Cake size={14} style={{ color: AG.primary }} /> : <Award size={14} style={{ color: AG.primary }} />}
                {e.name} <span style={{ color: AG.n400 }}>· {e.kind === 'Birthday' ? `turns ${e.years}` : `${e.years} yr${e.years === 1 ? '' : 's'}`}</span>
              </span>
              <Pill tone={e.inDays === 0 ? 'success' : 'neutral'}>{eventLabel(e)}</Pill>
            </div>
          )} />
        <Card style={{ padding: 18 }}>
          <div className="flex items-center gap-2" style={{ marginBottom: 12 }}><Award size={17} style={{ color: AG.primary }} /><span style={{ fontWeight: 600, color: AG.text }}>OKR status</span></div>
          {okrEntries.length === 0 ? <p style={{ fontSize: 13, color: AG.n500 }}>No OKRs assigned to the team.</p> : (
            <div style={{ display: 'grid', gap: 8 }}>
              {okrEntries.map(([status, count]) => (
                <div key={status} className="flex items-center justify-between"><span style={{ fontSize: 13.5, color: AG.text }}>{status}</span><span style={{ fontSize: 14, fontWeight: 700, color: AG.text }}>{count}</span></div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
